import { useTranslation } from "react-i18next";
import { UseFormReturn } from "react-hook-form";
import { IPersonUserForm } from "../../Person";
import { Card, Input, Select } from "@velocity-ehs/component-library";
import { PersonCustomerRelationship } from "../../PersonForm";
import classes from "../../cards/PersonDetailsCard.module.scss";
import * as Permissions from "../../../../supportFunctions/Permissions";
import UsersService from "@velocity-ehs/user-utils-module";
import { useFlags } from "launchdarkly-react-client-sdk";
import { emailRegExp } from "../../../../supportFunctions/ValidationHelpers";

interface AdditionalInformationProps {
  formMethods: UseFormReturn<IPersonUserForm, unknown>;
  openSlider?: () => void;
  contractingCompanies?: Array<{ label: string; id: string; value: string }>;
}

const UserDetails = ({
  formMethods,
  contractingCompanies,
  openSlider,
}: AdditionalInformationProps) => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
    watch,
  } = formMethods;

  const relationshipToCustomer = watch("relationshipToCustomer");
  const isContractor = () => {
    if (typeof relationshipToCustomer === "string") {
      return relationshipToCustomer === "CONTRACTOR";
    }

    return relationshipToCustomer?.id === "CONTRACTOR";
  };

  return (
    <Card title={t("person_user_details")} style={{ paddingBottom: "2px" }}>
      <Input
        control={control}
        name="displayName"
        label={t("universal_display_name")}
        rules={{
          required: t("form_message_Display_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.displayName?.message}
      />
      <Input
        control={control}
        name="firstName"
        label={t("universal_First_Name")}
        rules={{
          required: t("customer_Form_Admin_User_First_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.firstName?.message}
      />
      <Input
        control={control}
        name="lastName"
        label={t("universal_Last_Name")}
        rules={{
          required: t("customer_Form_Admin_User_Last_Name_Required"),
          maxLength: {
            value: 100,
            message: t("max_length_100_char_form_validation"),
          },
        }}
        errorMessage={errors && errors.lastName?.message}
      />
      <Input
        control={control}
        name="username"
        label={t("universal_Username")}
        rules={{
          required: t("customer_Form_Admin_User_Username_Required"),
          pattern: {
            value: /^\s*[^+\\;,]+\s*$/i,
            message: t("message_invalid_username"),
          },
          maxLength: {
            value: 64,
            message: t(
              "customer_Form_Admin_User_Username_Max_Length_Validation"
            ),
          },
        }}
        errorMessage={errors ? errors.username?.message : undefined}
      />
      <Input
        control={control}
        name="email"
        label={t("universal_email_address_text")}
        rules={{
          required: t("customer_Form_Admin_User_Email_Required"),
          pattern: {
            value: emailRegExp,
            message: t("message_invalid_email_address"),
          },
        }}
        errorMessage={errors ? errors.email?.message : undefined}
      />
      <Select
        control={control}
        name="relationshipToCustomer"
        label={t("person_Customer_Relationship")}
        options={PersonCustomerRelationship()}
        required
        errorMessage={
          errors ? errors.relationshipToCustomer?.message : undefined
        }
      />
      {isContractor() ? (
        <>
          <Select
            control={control}
            display="name"
            key="contractingCompany"
            name="contractingCompany"
            label={t("person_contracting_company")}
            options={contractingCompanies}
            required
            errorMessage={
              errors ? errors.contractingCompany?.message : undefined
            }
          />
          {Permissions.IsAllowed(
            [Permissions.canEditContractingCompanies],
            UsersService.permissions
          ) ? (
            <button
              className={classes.addContractingCompanyButton}
              type="button"
              onClick={() => openSlider()}
            >
              {t("contracting_companies_add_text")}
            </button>
          ) : null}
        </>
      ) : null}
    </Card>
  );
};

export { UserDetails };
